<template>
  <li
    v-if="isMaster"
    class="nav-item ml-auto d-flex align-items-center"
  >
    <div
      v-b-tooltip.hover
      :title="hint"
      class="d-flex align-items-center border rounded mirror-frame"
      :class="frameClass"
    >
      <feather-icon
        v-if="enabled && trouble"
        icon="AlertTriangleIcon"
        size="15"
        class="text-warning mr-50"
      />
      <span
        class="mr-50 mirror-label"
        :class="{ 'text-muted': !enabled }"
      >
        {{ $t('อัปเดตตามระบบจริง') }}
      </span>
      <b-form-checkbox
        v-model="model"
        :disabled="loading || saving"
        switch
        inline
        class="mr-0"
        @change="onToggle"
      />
    </div>
  </li>
</template>

<script>
import { BFormCheckbox, VBTooltip } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import lottoService from '@/service/lottoService'

export default {
  components: {
    BFormCheckbox,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      enabled: false,
      model: false,
      loading: true,
      saving: false,
      confirming: false,
      lastSyncAt: '',
      lastError: '',
      staleSeconds: null,
      timer: null,
    }
  },
  computed: {
    isMaster() {
      return this.$store.state.app.isMaster(this.$store)
    },
    // กรอบเปลี่ยนสีตามสถานะแบบเดียวกับตารางเรทที่ใช้ border-warning บอกค่าที่ต่ำกว่าต้นทาง
    frameClass() {
      if (!this.enabled) return ''
      // ใส่พื้นอ่อนด้วย ไม่งั้นความต่างระหว่างเปิดกับปิดเหลือแค่เส้นขอบหนา 1px ซึ่งมองข้ามได้ง่าย
      //
      // ใช้ warning ไม่ใช่ danger เพราะ primary ของธีมนี้เป็นแดงเข้ม (#b5122b) ส่วน danger เป็นแดงอ่อน (#ea5455)
      // สองสีนี้วางเทียบกันแล้วแยกไม่ออก กรอบที่เปลี่ยนสีเพื่อบอกว่ามีปัญหาจึงบอกอะไรไม่ได้เลย
      return this.trouble ? 'border-warning bg-light-warning' : 'border-primary bg-light-primary'
    },
    // ตัวเกาะตายหรือค้างจะไม่เขียนอะไรใหม่เลย ทั้งข้อความและเวลาอ่านล่าสุดจึงค้างค่าที่ดูปกติดี
    // ซึ่งเป็นรากของบั๊กที่เคยค้างสี่เดือนโดยไม่มีใครรู้ ความสดของค่าจึงต้องเป็นสัญญาณเตือนด้วยตัวมันเอง
    silent() {
      return this.staleSeconds !== null && this.staleSeconds > 90
    },
    trouble() {
      return !!this.lastError || this.silent
    },
    // ข้อความของฝั่งเซิร์ฟเวอร์มาก่อน เพราะบอกสาเหตุได้ตรงกว่าการบอกว่าเงียบไปเฉย ๆ
    notice() {
      if (this.lastError) return this.lastError
      if (!this.silent) return ''

      const minutes = Math.floor(this.staleSeconds / 60)
      return minutes > 0
        ? this.$t('ตัวเกาะไม่ได้อ่านระบบจริงมา {minutes} นาที ตรวจ huay-worker', { minutes })
        : this.$t('ตัวเกาะไม่ได้อ่านระบบจริงมาสักพัก ตรวจ huay-worker')
    },
    hint() {
      if (!this.enabled) return this.$t('สั่งรอบเองทั้งหมด')

      const state = this.$t('รอบเดินตามระบบจริง ปุ่มสั่งรอบถูกล็อก')
      if (this.silent) return `${state} · ${this.notice}`

      return this.lastSyncAt ? `${state} · ${this.$t('อ่านล่าสุด')} ${this.lastSyncAt}` : state
    },
  },
  mounted() {
    if (!this.isMaster) return
    this.load()
    // ตอนเกาะอยู่ เวลาอ่านล่าสุดคือสัญญาณเดียวที่บอกว่าตัวเกาะยังทำงาน ค่าที่ค้างอยู่เฉย ๆ บอกอะไรไม่ได้
    this.timer = setInterval(() => {
      if (this.enabled) this.load()
    }, 15000)
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    async load() {
      const res = await lottoService.getAuthen('huay-mirror')
      // ปลดล็อกปุ่มไม่ว่าผลจะเป็นอย่างไร ไม่งั้นคำขอแรกที่พลาดจะทำให้สวิตช์กดไม่ได้ตลอดอายุหน้าจอ
      this.loading = false
      if (res.status !== 200) return

      this.enabled = res.data.enabled
      // กล่องยืนยันเปิดค้างอยู่ = ตำแหน่งสวิตช์เป็นของผู้ใช้ ไม่ใช่ของเซิร์ฟเวอร์
      if (!this.confirming) this.model = res.data.enabled
      this.lastSyncAt = res.data.lastSyncAt || ''
      this.lastError = res.data.lastError || ''
      this.staleSeconds = res.data.staleSeconds === undefined ? null : res.data.staleSeconds

      // ข้อความของตัวเกาะยาวเกินกว่าจะอยู่ในแถบแท็บ และเป็นเรื่องที่ต้องเห็นโดยไม่ต้องเอาเมาส์ไปชี้
      // ตัวสวิตช์บอกได้แค่ว่ามีเรื่อง ส่วนเนื้อความส่งให้หน้าที่วางมันไว้เป็นคนแสดง
      this.$emit('notice', this.enabled ? this.notice : '')
    },
    onToggle(next) {
      this.confirming = true
      this.$swal({
        dark: true,
        title: next ? this.$t('เปิดการอัปเดตตามระบบจริง?') : this.$t('ปิดการอัปเดตตามระบบจริง?'),
        text: next
          ? this.$t('รอบที่เปิดค้างและไม่ตรงกับระบบจริงจะถูกคืนเงินทั้งรอบ จากนั้นทุกรอบเดินตามระบบจริง ปุ่มสั่งรอบถูกล็อก')
          : this.$t('ปิดแล้วสั่งรอบเองทั้งหมด รอบที่ค้างอยู่เดินต่อตามเดิม'),
        // เปิดแล้วมีเครดิตขยับจริง กล่องยืนยันจึงต้องเป็นคำเตือน ไม่ใช่คำบอกเล่า
        icon: next ? 'warning' : 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('Ok'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        this.confirming = false

        if (!result.value) {
          this.model = this.enabled
          return
        }

        this.saving = true
        const res = await lottoService.putAuthen('huay-mirror', { enabled: next })
        this.saving = false

        if (res.status !== 200) {
          this.model = this.enabled
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('เกิดข้อผิดพลาด'),
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: res.data,
            },
          })
          return
        }

        this.enabled = next
        this.lastError = ''
        this.lastSyncAt = ''
        this.staleSeconds = null
        this.$emit('notice', '')
        await this.load()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// กล่องของสวิตช์กว้าง 3.5rem แต่ธีมวาดรางแค่ 3rem ส่วนต่างคือที่เผื่อไว้ให้ตัวหนังสือซึ่งเราไม่ได้ใส่
// จึงกลายเป็นที่ว่างหลังสวิตช์ ขยายรางให้เต็มกล่องแทนการไปหดกล่อง เพราะกล่องมาจาก padding ของ label
$track-width: 3.5rem; // 49px เท่า padding-left ของ label พอดี รางจึงเต็มกล่อง ไม่เหลือที่ว่างหลังสวิตช์
$track-height: 1.7rem; // 23.8px
$knob-inset: 4px; // ธีมตรึงค่านี้ไว้จนเขียนทับไม่ขึ้น จึงคำนวณขนาดปุ่มจากค่านี้แทนการฝืน
$knob-size: 15.8px; // 23.8 - 4x2 ปุ่มจึงเหลือขอบเท่ากันครบสี่ด้าน

.mirror-frame {
  padding: 0.35rem 0.6rem;
}

// สูงเท่ารางสวิตช์ ตัวหนังสือกับสวิตช์จึงมีจุดกึ่งกลางเดียวกันจริง ไม่ใช่ใกล้เคียง
.mirror-label {
  font-size: 0.9rem;
  line-height: $track-height;
}

// label ไม่มีตัวหนังสือข้างใน กล่องจึงสูงศูนย์ แล้วถูก min-height ของ bootstrap ดันขึ้นมาเป็น 20.3px
// ซึ่งเตี้ยกว่าราง 3.5px รางที่ยึด top: 0 กับกล่องเลยห้อยต่ำกว่ากึ่งกลางตัวหนังสือไป 1.75px
// ตรึงกล่องให้สูงเท่ารางเสียเลย ทุกอย่างข้างในจึงลงกึ่งกลางเองโดยไม่ต้องขยับชดเชย
::v-deep .custom-control.custom-switch {
  height: $track-height;
}

::v-deep .custom-control.custom-switch .custom-control-label {
  // ธีมแถม margin ล่าง 0.5rem ให้สวิตช์ที่วางเป็นแถวตั้ง มันรั่วออกมานอกกล่องแล้วดันกล่องสวิตช์สูงเกินจริง
  margin-bottom: 0;

  // label เป็น inline ตัวรางที่วางแบบ absolute จึงยึดกับกรอบตัวอักษรของ label ไม่ใช่ตัวกล่อง
  // รางเลยตกต่ำกว่ากึ่งกลางไปเท่ากับช่องไฟบนของบรรทัด บังคับเป็น block กรอบทั้งสองจึงเป็นอันเดียวกัน
  display: block;

  &::before {
    width: $track-width;
    height: $track-height;
  }

  // ปุ่มกลมของธีมวางด้วย top ตายตัว 4px ซึ่งไม่ตรงกลางราง ขนาดชุดนี้คำนวณให้ตกกลางพอดีทั้งสองด้าน
  &::after {
    top: $knob-inset;
    left: $knob-inset;
    width: $knob-size;
    height: $knob-size;
  }
}

::v-deep .custom-switch .custom-control-input:checked ~ .custom-control-label::after {
  // 49 - 4x2 - 15.8 = 25.2px พอดี 1.8rem (คำนวณตรง ๆ ไม่ได้เพราะคนละหน่วย)
  transform: translateX(1.8rem);
}
</style>
